import type { IsoKey } from "./illustrations";

export type AddonStep = {
  title: string;
  body: string;
  iso: IsoKey;
};

export type AddonHowItWorks = {
  lead: string;
  steps: AddonStep[];
};

/**
 * "How it works" narrative for each add-on — 3–4 steps, each headed by one of
 * the isometric illustrations from the brand pack.
 */
export const ADDON_HOW_IT_WORKS: Record<string, AddonHowItWorks> = {
  wms: {
    lead: "From receiving to shipping — every move is scanned, located and traceable.",
    steps: [
      {
        title: "Receive and put away",
        body: "Inbound deliveries are matched to purchase orders, labelled and routed to a storage location suggested by the system.",
        iso: "supplyChain",
      },
      {
        title: "Track every location",
        body: "Bins, racks and shop-floor buffers are mapped one-to-one with stock records, so quantities always reflect physical reality.",
        iso: "smartFactory",
      },
      {
        title: "Pick, pack, ship",
        body: "Pick lists are generated from orders and production demand; scanning confirms each line before dispatch.",
        iso: "roboticProduction",
      },
      {
        title: "Measure and correct",
        body: "Cycle counts, movement history and accuracy reports show where losses happen and how to close them.",
        iso: "reporting",
      },
    ],
  },
  "cable-plm": {
    lead: "Turn cable and harness engineering into reusable, cost-aware product data.",
    steps: [
      {
        title: "Model the construction",
        body: "Describe conductors, insulation, screens and sheaths once — the structure becomes a versioned product definition.",
        iso: "roboticProduction",
      },
      {
        title: "Generate BOM and routing",
        body: "Materials, weights and process steps are calculated from the construction instead of retyped in spreadsheets.",
        iso: "planner",
      },
      {
        title: "Cost and quote instantly",
        body: "Copper, polymer and labour rates flow into the cost model, so a quote reflects today's prices, not last quarter's.",
        iso: "marketAnalysis",
      },
      {
        title: "Release to production",
        body: "Approved revisions are pushed to production orders and technical data with a full change history.",
        iso: "smartFactory",
      },
    ],
  },
  pdm: {
    lead: "One controlled home for drawings, specifications and revisions.",
    steps: [
      {
        title: "Capture documents",
        body: "Drawings, CAD exports and specs are attached to items and operations instead of living in shared folders.",
        iso: "reporting",
      },
      {
        title: "Control revisions",
        body: "Every change gets a revision, an author and an approval trail; superseded files stay readable but clearly retired.",
        iso: "storytelling",
      },
      {
        title: "Publish to the floor",
        body: "Operators always open the current revision from the work order — no printed copies to go stale.",
        iso: "smartFactory",
      },
    ],
  },
  crm: {
    lead: "Keep the commercial pipeline and the factory on the same set of facts.",
    steps: [
      {
        title: "Capture the lead",
        body: "Inbound requests, referrals and outbound activity land in one pipeline with owners and next steps.",
        iso: "targeting",
      },
      {
        title: "Quote from real data",
        body: "Quotations use live product structures, costs and capacity — not a sales-only price list.",
        iso: "investment",
      },
      {
        title: "Convert to orders",
        body: "A won deal becomes a sales order and demand signal without re-entry, so planning sees it immediately.",
        iso: "teamwork",
      },
      {
        title: "Grow the account",
        body: "Order history, margins and service records inform renewals, upsell and churn risk.",
        iso: "marketAnalysis",
      },
    ],
  },
  "sales-operations-planning": {
    lead: "Balance demand, capacity and inventory in one monthly rhythm.",
    steps: [
      {
        title: "Build the demand picture",
        body: "Forecasts, open orders and pipeline are consolidated into a single demand plan per family.",
        iso: "targeting",
      },
      {
        title: "Test it against capacity",
        body: "Scenarios show what happens to lead times, stock and cost if volumes shift or a line goes down.",
        iso: "strategy",
      },
      {
        title: "Agree one plan",
        body: "Sales, operations and finance sign off the same numbers, with assumptions documented.",
        iso: "teamwork",
      },
      {
        title: "Track the gap",
        body: "Actuals are compared to plan every cycle so bias and blind spots surface early.",
        iso: "reporting",
      },
    ],
  },
  "ai-agents": {
    lead: "Agents watch the data you already produce and turn it into decisions.",
    steps: [
      {
        title: "Connect to live data",
        body: "Agents read orders, stock, routings and shop-floor events straight from the platform — no separate warehouse.",
        iso: "sendMessage",
      },
      {
        title: "Detect and explain",
        body: "Deviations, late risks and margin drops are flagged with the reasoning and the records behind them.",
        iso: "strategy",
      },
      {
        title: "Propose the next action",
        body: "Reschedule, expedite, re-quote — suggestions arrive as concrete actions a human can approve.",
        iso: "robotics",
      },
      {
        title: "Learn from outcomes",
        body: "Accepted and rejected suggestions tune what the agent surfaces next time.",
        iso: "wow",
      },
    ],
  },
  "project-management": {
    lead: "Run engineered-to-order work as projects that stay tied to production.",
    steps: [
      {
        title: "Structure the project",
        body: "Phases, milestones and deliverables are defined once and linked to sales and production orders.",
        iso: "kanban",
      },
      {
        title: "Plan the work",
        body: "Tasks, owners and dependencies show the critical path across engineering, procurement and the shop floor.",
        iso: "planner",
      },
      {
        title: "Track time and cost",
        body: "Hours, materials and subcontracting book against the project, so margin is visible while there is still time to react.",
        iso: "investment",
      },
      {
        title: "Close and learn",
        body: "Plan versus actual per phase feeds the next estimate instead of disappearing into a spreadsheet.",
        iso: "reporting",
      },
    ],
  },
  services: {
    lead: "Field service, installation and maintenance managed on the same platform as production.",
    steps: [
      {
        title: "Register the request",
        body: "Service calls, warranty claims and planned maintenance arrive in one queue with equipment history attached.",
        iso: "sendMessage",
      },
      {
        title: "Schedule the crew",
        body: "Technicians, skills, spare parts and travel are planned together so a visit is not wasted.",
        iso: "planner",
      },
      {
        title: "Execute on site",
        body: "Checklists, consumed parts and photos are captured in the field and synced back to the order.",
        iso: "robotics",
      },
      {
        title: "Bill and analyse",
        body: "Time and materials convert into invoices; recurring failures feed back into engineering.",
        iso: "marketAnalysis",
      },
    ],
  },
  "extra-user": {
    lead: "Add people to the platform without a new project.",
    steps: [
      {
        title: "Add the seat",
        body: "Extra named users are activated on your existing subscription and billed per user, per month.",
        iso: "teamwork",
      },
      {
        title: "Assign roles",
        body: "Each user gets the permissions of their role — shop floor, planner, buyer, finance or management.",
        iso: "strategy",
      },
      {
        title: "Onboard fast",
        body: "Role-based views mean a new user sees only their work, so training stays short.",
        iso: "storytelling",
      },
    ],
  },
  "extra-storage-location": {
    lead: "Extend stock control to more sites, warehouses and buffers.",
    steps: [
      {
        title: "Define the location",
        body: "A new warehouse, external store or shop-floor buffer is added to the stock model.",
        iso: "supplyChain",
      },
      {
        title: "Route the movements",
        body: "Transfers, replenishment and reservations start using the new location right away.",
        iso: "smartFactory",
      },
      {
        title: "Report across sites",
        body: "Availability and valuation stay consolidated, with drill-down per location.",
        iso: "reporting",
      },
    ],
  },
};

/** Fallback narrative for add-ons without a curated one. */
export const ADDON_HOW_IT_WORKS_FALLBACK: AddonHowItWorks = {
  lead: "A short path from setup to measurable result.",
  steps: [
    {
      title: "Set it up with us",
      body: "We switch the add-on on inside your existing environment and map it to how you already work.",
      iso: "storytelling",
    },
    {
      title: "Connect your data",
      body: "Items, orders and history from the core platform feed the add-on — no parallel database.",
      iso: "supplyChain",
    },
    {
      title: "Run and measure",
      body: "Your team works in one place and the results show up in the same reports as the rest of the factory.",
      iso: "reporting",
    },
  ],
};

export function addonHowItWorks(slug: string): AddonHowItWorks {
  return ADDON_HOW_IT_WORKS[slug] ?? ADDON_HOW_IT_WORKS_FALLBACK;
}
